import React, { useEffect, useState } from "react";
import api, { fmt, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Check, X as XIcon, ShieldCheck } from "@phosphor-icons/react";

export default function ApprovalsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [msg, setMsg] = useState("");
  const canApprove = user && ["admin", "checker"].includes(user.role);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    const { data } = await api.get("/approvals");
    setRows(data.transactions || []);
    if (isAdmin) {
      const u = await api.get("/users");
      setUsers(u.data);
    }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, action) => {
    try {
      await api.post(`/approvals/${id}/${action}`);
      setMsg(`Transaction ${action}d`);
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed");
    }
  };

  const setRole = async (email, role) => {
    await api.post("/users/role", { email, role });
    setMsg(`Role updated for ${email}`);
    load();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Workflow"
        title="Approvals Queue"
        subtitle={canApprove ? "Approve or reject pending transactions submitted by makers." : "Read-only — only Checker or Admin roles can approve."}
        testid="approvals-title"
      />

      {msg && <div className="mx-8 mt-6 border border-gray-300 bg-gray-50 px-4 py-3 text-xs font-mono" data-testid="approval-msg">{msg}</div>}

      <div className="p-8 space-y-6">
        <div className="border border-gray-200 bg-white">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Pending</div>
              <h3 className="text-xl font-black tracking-tight mt-0.5">Transactions Awaiting Approval</h3>
            </div>
            <div className="text-xs font-mono text-gray-500" data-testid="pending-count">{rows.length} pending</div>
          </div>
          <table className="w-full text-sm" data-testid="pending-table">
            <thead className="bg-gray-50"><tr className="text-left">
              {["Date","Type","Reference","Amount","Ccy","Created by",""].map((h) => <th key={h} className={`px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300 ${h === "Amount" ? "text-right" : ""}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs">{fmtDate(t.value_date)}</td>
                  <td className="px-4 py-3 text-[10px] tracking-[0.15em] uppercase font-bold">{t.txn_type.replace(/_/g, " ")}</td>
                  <td className="px-4 py-3 font-mono text-xs">{t.reference || "—"}</td>
                  <td className={`px-4 py-3 text-right font-mono ${(t.direction_sign ?? 0) < 0 ? "text-[#DC2626]" : ""}`}>{fmt(t.amount)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{t.currency}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{t.created_by || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {canApprove ? (
                      <>
                        <button onClick={() => decide(t.id, "approve")} data-testid={`approve-${t.id}`} className="bg-[#0020C2] text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:bg-[#001AA3] inline-flex items-center gap-1 mr-2"><Check size={12} weight="bold" /> Approve</button>
                        <button onClick={() => decide(t.id, "reject")} data-testid={`reject-${t.id}`} className="border border-[#DC2626] text-[#DC2626] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:bg-red-50 inline-flex items-center gap-1"><XIcon size={12} weight="bold" /> Reject</button>
                      </>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider text-gray-400">No permission</span>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">No pending transactions.</td></tr>}
            </tbody>
          </table>
        </div>

        {isAdmin && (
          <div className="border border-gray-200 bg-white">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
              <ShieldCheck size={18} weight="bold" />
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Admin</div>
                <h3 className="text-xl font-black tracking-tight mt-0.5">User Roles</h3>
              </div>
            </div>
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-gray-50"><tr className="text-left">
                {["Email","Name","Company","Role"].map((h) => <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>)}
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-gray-100">
                    <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3">{u.name}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{u.company || "—"}</td>
                    <td className="px-4 py-3">
                      <select
                        defaultValue={u.role}
                        onChange={(e) => setRole(u.email, e.target.value)}
                        data-testid={`role-select-${u.email}`}
                        className="border border-gray-300 px-2 py-1 text-xs font-mono"
                      >
                        {["admin", "maker", "checker", "user"].map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">No users.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
